import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Key, Copy, Ban, Check } from "lucide-react";

const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

export default function TokensTab() {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/admin/api-tokens").then((r) => setTokens(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!name.trim()) return toast.error("Give the token a name");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/api-tokens", { name: name.trim() });
      setCreated(data);
      setName("");
      toast.success("API token created — copy it now, it won't be shown again");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Copy failed — select the text manually");
    }
  };

  const revoke = async (t) => {
    if (!window.confirm(`Revoke token "${t.name}"? Connected software will stop working immediately.`)) return;
    try {
      await api.delete(`/admin/api-tokens/${t.id}`);
      toast.success("Token revoked");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-tokens-tab">
      <AdminHeader testid="admin-header-tokens" eyebrow="System" title="API Tokens" description="Keys that let other software read your fleet and bookings." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6 h-fit">
          <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
              <Key size={14} className="text-[#C71E2E]" /> New API Token
            </p>
            <div className="mt-5 space-y-4">
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Token name</Label>
                <Input data-testid="token-form-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Partner website" className="input-dark mt-1.5" />
              </div>
              <Button data-testid="token-submit-button" onClick={save} disabled={saving} className="w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">
                {saving ? "Creating…" : "Create Token"}
              </Button>
            </div>
          </div>

          {created && (
            <div data-testid="token-created-box" className="bg-[#1A1A1A] text-white rounded-2xl p-6">
              <p className="font-ui text-xs tracking-[0.2em] uppercase text-amber-300">Copy now — shown once</p>
              <p data-testid="token-created-value" className="mt-3 font-mono text-xs break-all bg-black/40 rounded-xl p-3 border border-white/10">{created.token}</p>
              <Button data-testid="token-copy-button" onClick={copy} className="mt-4 w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">
                {copied ? <><Check size={15} className="mr-2" /> Copied</> : <><Copy size={15} className="mr-2" /> Copy Token</>}
              </Button>
              <p className="mt-4 text-[11px] text-white/60 leading-relaxed">
                Send it as the <span className="font-mono text-white/80">X-API-Key</span> header to
                <span className="font-mono text-white/80"> GET /api/external/vehicles</span> or
                <span className="font-mono text-white/80"> GET /api/external/bookings</span>.
              </p>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto h-fit">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5DED5] hover:bg-transparent">
                <TableHead>Name</TableHead>
                <TableHead>Token</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.length === 0 && (
                <TableRow className={ROW}>
                  <TableCell colSpan={6} data-testid="tokens-empty" className="text-muted-foreground font-ui py-10 text-center">No API tokens yet.</TableCell>
                </TableRow>
              )}
              {tokens.map((t) => (
                <TableRow key={t.id} data-testid={`token-row-${t.id}`} className={ROW}>
                  <TableCell className="font-ui font-semibold text-[#1A1A1A]">{t.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{t.prefix}…</TableCell>
                  <TableCell className="text-muted-foreground font-ui text-xs">{fmtDate(t.created_at?.slice(0, 10))}</TableCell>
                  <TableCell className="text-muted-foreground font-ui text-xs">{t.last_used_at ? fmtDate(t.last_used_at.slice(0, 10)) : "Never"}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${t.revoked ? "text-red-700 border-red-200 bg-red-50" : "text-emerald-700 border-emerald-200 bg-emerald-50"}`}>
                      {t.revoked ? "Revoked" : "Active"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {!t.revoked && (
                      <Button data-testid={`token-revoke-${t.id}`} size="sm" variant="outline" onClick={() => revoke(t)} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-red-50 hover:text-red-700 font-ui">
                        <Ban size={13} className="mr-1.5" /> Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
